/**
 * Go-live readiness — READ-ONLY, flag-gated pre-flight card (increment #24).
 *
 * Sources GET /job-connections/go-live-readiness (JWT), the read-only in-app
 * report behind the backend flag SAIK_GO_LIVE_READINESS_ENABLED. While the flag
 * is OFF the endpoint answers the inert `{ok:true, enabled:false}` (no report
 * body) and this card renders NOTHING, so it is INERT in production. It also
 * renders nothing while loading, on request error, or on timeout.
 *
 * When the flag is ON the endpoint returns the raw report body
 * (state/fatal/warn/info/alembic/flags/routes) and this card shows ONE Card
 * titled "Go-live readiness" with:
 *  - a state badge (OK / WARN / FATAL)
 *  - "N flags inert / M armed" (from report.flags.rows / .armed)
 *  - "R of T gated routes registered" (from report.routes.registered / .expected)
 *  - the fatal / warn counts when non-zero
 *
 * Display-only: the only network is the single GET. It renders NAMES, booleans
 * and COUNTS only — never a raw secret/token/password.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import Card from './Card';
import { jobbotApi, type GoLiveReadinessResponse } from '../api/jobbot';

/** Keep a slow/dead backend from ever leaving a stray placeholder on screen. */
const REQUEST_TIMEOUT_MS = 8000;

/** Human tone per readiness state. */
const STATE_TONE: Record<string, string> = {
  OK: 'bg-green-500/20 text-green-700 dark:text-green-400',
  WARN: 'bg-amber-500/20 text-amber-700 dark:text-amber-400',
  FATAL: 'bg-red-500/20 text-red-700 dark:text-red-400',
};

export default function GoLiveReadinessCard() {
  const [report, setReport] = useState<GoLiveReadinessResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    (async () => {
      try {
        const res = await jobbotApi.extension.goLiveReadiness({ signal: controller.signal });
        const data = res.data;
        if (!alive) return;
        // Flag OFF → endpoint returns {ok:true, enabled:false} → render nothing.
        // Flag ON → the report body carries a `state` and no `enabled` key.
        const off = data?.enabled === false;
        setReport(!off && data?.state ? data : null);
      } catch {
        // Network error / timeout / disabled → render nothing.
        if (alive) setReport(null);
      } finally {
        window.clearTimeout(timer);
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, []);

  if (loading) return null; // never flash a placeholder before we know the flag
  if (!report) return null; // flag OFF / error / timeout → inert

  const state = report.state ?? 'WARN';
  const flagRows = report.flags?.rows?.length ?? 0;
  const armed = report.flags?.armed?.length ?? 0;
  const inertFlags = Math.max(flagRows - armed, 0);
  const registered = report.routes?.registered?.length ?? 0;
  const expected = report.routes?.expected?.length ?? 0;
  const fatalCount = report.fatal?.length ?? 0;
  const warnCount = report.warn?.length ?? 0;

  return (
    <Card className="mb-6 p-4" data-agent-id="jobbot.sites.go-live-readiness">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck size={18} className="text-brand-600" aria-hidden="true" />
        <h2 className="font-semibold text-slate-950 dark:text-slate-100">Go-live readiness</h2>
        <span
          className={`ml-auto inline-block rounded-md px-2 py-0.5 text-xs font-semibold ${STATE_TONE[state] || STATE_TONE.WARN}`}
          data-agent-id="jobbot.sites.go-live-readiness.state"
        >
          {state}
        </span>
      </div>
      <p
        className="text-sm text-slate-600 dark:text-slate-400"
        data-agent-id="jobbot.sites.go-live-readiness.flags"
      >
        {inertFlags} flags inert / {armed} armed
      </p>
      <p
        className="mt-1 text-sm text-slate-600 dark:text-slate-400"
        data-agent-id="jobbot.sites.go-live-readiness.routes"
      >
        {registered} of {expected} gated routes registered
      </p>
      {(fatalCount > 0 || warnCount > 0) && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">
          {fatalCount > 0 && (
            <span className="text-red-700 dark:text-red-400">{fatalCount} fatal</span>
          )}
          {fatalCount > 0 && warnCount > 0 && ' · '}
          {warnCount > 0 && (
            <span className="text-amber-700 dark:text-amber-400">{warnCount} warn</span>
          )}
        </p>
      )}
    </Card>
  );
}
